"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DependencyManager = exports.NexusDependencyManager = exports.VersionComparator = exports.DependencyInjector = exports.DependencyStatus = void 0;
exports.Injectable = Injectable;
exports.Inject = Inject;
/**
 * 依赖状态
 */
var DependencyStatus;
(function (DependencyStatus) {
    DependencyStatus["RESOLVED"] = "resolved";
    DependencyStatus["PENDING"] = "pending";
    DependencyStatus["MISSING"] = "missing";
    DependencyStatus["INCOMPATIBLE"] = "incompatible";
    DependencyStatus["CIRCULAR"] = "circular";
})(DependencyStatus || (exports.DependencyStatus = DependencyStatus = {}));
/**
 * 依赖注入容器
 */
class DependencyInjector {
    constructor() {
        this.providers = new Map();
        this.instances = new Map();
        this.resolving = new Set();
    }
    /**
     * 注册提供者
     */
    register(provider) {
        this.providers.set(provider.provide, provider);
    }
    /**
     * 解析依赖
     */
    resolve(token) {
        // 检查是否已有实例
        if (this.instances.has(token)) {
            return this.instances.get(token);
        }
        // 检查循环依赖
        if (this.resolving.has(token)) {
            throw new Error(`循环依赖检测: ${this.getTokenDescription(token)}`);
        }
        // 获取提供者
        const provider = this.providers.get(token);
        if (!provider) {
            throw new Error(`未找到提供者: ${this.getTokenDescription(token)}`);
        }
        // 开始解析
        this.resolving.add(token);
        try {
            let instance;
            if (provider.useValue !== undefined) {
                instance = provider.useValue;
            }
            else if (provider.useFactory) {
                const deps = provider.deps ? provider.deps.map(dep => this.resolve(dep)) : [];
                instance = provider.useFactory(...deps);
            }
            else if (provider.useClass) {
                const deps = provider.deps ? provider.deps.map(dep => this.resolve(dep)) : [];
                instance = new provider.useClass(...deps);
            }
            else {
                throw new Error(`无效的提供者配置: ${this.getTokenDescription(token)}`);
            }
            // 缓存实例
            this.instances.set(token, instance);
            return instance;
        }
        finally {
            this.resolving.delete(token);
        }
    }
    /**
     * 获取令牌描述
     */
    getTokenDescription(token) {
        if (typeof token === 'string') {
            return token;
        }
        return token.description || token.toString();
    }
    /**
     * 清空容器
     */
    clear() {
        this.providers.clear();
        this.instances.clear();
        this.resolving.clear();
    }
}
exports.DependencyInjector = DependencyInjector;
/**
 * 版本比较工具
 */
class VersionComparator {
    /**
     * 解析版本字符串
     */
    static parseVersion(version) {
        const match = version.match(/^(\d+)\.(\d+)\.(\d+)(?:-([a-zA-Z0-9\-]+))?(?:\+([a-zA-Z0-9\-]+))?$/);
        if (!match) {
            throw new Error(`无效的版本格式: ${version}`);
        }
        return {
            major: parseInt(match[1]),
            minor: parseInt(match[2]),
            patch: parseInt(match[3]),
            preRelease: match[4],
            build: match[5]
        };
    }
    /**
     * 比较版本
     * @returns -1 if a < b, 0 if a == b, 1 if a > b
     */
    static compareVersions(a, b) {
        const versionA = typeof a === 'string' ? this.parseVersion(a) : a;
        const versionB = typeof b === 'string' ? this.parseVersion(b) : b;
        // 比较主版本
        if (versionA.major !== versionB.major) {
            return versionA.major - versionB.major;
        }
        // 比较次版本
        if (versionA.minor !== versionB.minor) {
            return versionA.minor - versionB.minor;
        }
        // 比较补丁版本
        if (versionA.patch !== versionB.patch) {
            return versionA.patch - versionB.patch;
        }
        // 比较预发布版本
        if (versionA.preRelease && versionB.preRelease) {
            return versionA.preRelease.localeCompare(versionB.preRelease);
        }
        else if (versionA.preRelease) {
            return -1; // 预发布版本 < 正式版本
        }
        else if (versionB.preRelease) {
            return 1; // 正式版本 > 预发布版本
        }
        return 0;
    }
    /**
     * 检查版本是否兼容
     */
    static isCompatible(required, actual) {
        try {
            const requiredVersion = this.parseVersion(required);
            const actualVersion = this.parseVersion(actual);
            // 主版本必须完全匹配
            if (requiredVersion.major !== actualVersion.major) {
                return false;
            }
            // 次版本必须 >= 要求的版本
            if (actualVersion.minor < requiredVersion.minor) {
                return false;
            }
            // 如果次版本相同，补丁版本必须 >= 要求的版本
            if (actualVersion.minor === requiredVersion.minor &&
                actualVersion.patch < requiredVersion.patch) {
                return false;
            }
            return true;
        }
        catch (_a) {
            return false;
        }
    }
    /**
     * 获取版本范围内的最新版本
     */
    static getLatestCompatibleVersion(required, availableVersions) {
        const compatibleVersions = availableVersions.filter(version => this.isCompatible(required, version));
        if (compatibleVersions.length === 0) {
            return null;
        }
        return compatibleVersions.sort((a, b) => this.compareVersions(b, a))[0];
    }
}
exports.VersionComparator = VersionComparator;
/**
 * Nexus依赖管理器
 */
class NexusDependencyManager {
    constructor() {
        this.registeredDependencies = new Map();
        this.dependencyGraph = new Map();
        this.injector = new DependencyInjector();
        this.loadOrder = [];
        // 注册核心依赖
        this.registerCoreDependencies();
    }
    static getInstance() {
        if (!NexusDependencyManager.instance) {
            NexusDependencyManager.instance = new NexusDependencyManager();
        }
        return NexusDependencyManager.instance;
    }
    /**
     * 注册核心依赖
     */
    registerCoreDependencies() {
        // 注册主题管理器
        this.injector.register({
            provide: 'NexusThemeManager',
            useFactory: () => require('./NexusThemeManager').ThemeManager
        });
        // 注册错误管理器
        this.injector.register({
            provide: 'NexusErrorManager',
            useFactory: () => require('./NexusErrorManager').ErrorManager
        });
        // 注册依赖管理器本身
        this.injector.register({
            provide: 'NexusDependencyManager',
            useValue: this
        });
    }
    /**
     * 注册依赖
     */
    registerDependency(name, version, instance) {
        this.registeredDependencies.set(name, { version, instance });
        console.log(`依赖 "${name}" (${version}) 已注册`);
    }
    /**
     * 注册依赖图
     */
    registerDependencyGraph(pluginName, dependencies) {
        this.dependencyGraph.set(pluginName, dependencies);
    }
    /**
     * 解析依赖
     */
    resolveDependencies(pluginName) {
        const result = {
            resolved: new Map(),
            missing: [],
            incompatible: [],
            circular: []
        };
        const dependencies = this.dependencyGraph.get(pluginName) || [];
        const visited = new Set();
        const recursionStack = new Set();
        // 递归解析依赖
        const resolveDependency = (depName, depVersion) => {
            // 检查循环依赖
            if (recursionStack.has(depName)) {
                result.circular.push(depName);
                return;
            }
            // 检查是否已访问
            if (visited.has(depName)) {
                return;
            }
            visited.add(depName);
            recursionStack.add(depName);
            // 检查依赖是否存在
            const registeredDep = this.registeredDependencies.get(depName);
            if (!registeredDep) {
                result.missing.push(depName);
                recursionStack.delete(depName);
                return;
            }
            // 检查版本兼容性
            if (!VersionComparator.isCompatible(depVersion, registeredDep.version)) {
                result.incompatible.push({
                    name: depName,
                    required: depVersion,
                    actual: registeredDep.version
                });
                recursionStack.delete(depName);
                return;
            }
            // 解析成功
            result.resolved.set(depName, registeredDep);
            // 递归解析子依赖
            const subDependencies = this.dependencyGraph.get(depName) || [];
            for (const subDep of subDependencies) {
                resolveDependency(subDep.name, subDep.version);
            }
            recursionStack.delete(depName);
        };
        // 开始解析
        for (const dep of dependencies) {
            resolveDependency(dep.name, dep.version);
        }
        return result;
    }
    /**
     * 计算加载顺序
     */
    calculateLoadOrder(plugins) {
        const loadOrder = [];
        const visited = new Set();
        const recursionStack = new Set();
        const visit = (pluginName) => {
            if (recursionStack.has(pluginName)) {
                throw new Error(`循环依赖检测: ${pluginName}`);
            }
            if (visited.has(pluginName)) {
                return;
            }
            visited.add(pluginName);
            recursionStack.add(pluginName);
            // 先加载依赖
            const dependencies = this.dependencyGraph.get(pluginName) || [];
            for (const dep of dependencies) {
                if (plugins.includes(dep.name)) {
                    visit(dep.name);
                }
            }
            recursionStack.delete(pluginName);
            loadOrder.push(pluginName);
        };
        for (const plugin of plugins) {
            visit(plugin);
        }
        this.loadOrder = loadOrder;
        return loadOrder;
    }
    /**
     * 获取依赖实例
     */
    getDependency(name) {
        const dependency = this.registeredDependencies.get(name);
        return dependency === null || dependency === void 0 ? void 0 : dependency.instance;
    }
    /**
     * 注入依赖
     */
    inject(token) {
        return this.injector.resolve(token);
    }
    /**
     * 注册提供者
     */
    registerProvider(provider) {
        this.injector.register(provider);
    }
    /**
     * 获取依赖图
     */
    getDependencyGraph() {
        return new Map(this.dependencyGraph);
    }
    /**
     * 获取已注册的依赖
     */
    getRegisteredDependencies() {
        return new Map(this.registeredDependencies);
    }
    /**
     * 检查依赖健康状态
     */
    checkDependencyHealth() {
        const issues = [];
        // 检查所有插件的依赖
        for (const [pluginName, dependencies] of this.dependencyGraph) {
            const result = this.resolveDependencies(pluginName);
            // 检查缺失依赖
            for (const missing of result.missing) {
                issues.push({
                    type: 'missing',
                    plugin: pluginName,
                    dependency: missing,
                    message: `插件 ${pluginName} 缺少依赖 ${missing}`
                });
            }
            // 检查不兼容依赖
            for (const incompatible of result.incompatible) {
                issues.push({
                    type: 'incompatible',
                    plugin: pluginName,
                    dependency: incompatible.name,
                    message: `插件 ${pluginName} 依赖 ${incompatible.name} 版本不兼容 (需要: ${incompatible.required}, 实际: ${incompatible.actual})`
                });
            }
            // 检查循环依赖
            for (const circular of result.circular) {
                issues.push({
                    type: 'circular',
                    plugin: pluginName,
                    dependency: circular,
                    message: `插件 ${pluginName} 存在循环依赖 ${circular}`
                });
            }
        }
        return {
            healthy: issues.length === 0,
            issues
        };
    }
    /**
     * 获取依赖统计
     */
    getDependencyStats() {
        const stats = {
            totalDependencies: this.registeredDependencies.size,
            totalPlugins: this.dependencyGraph.size,
            averageDependenciesPerPlugin: 0,
            mostDependentPlugin: { name: '', count: 0 },
            circularDependencies: 0
        };
        let totalDependencyCount = 0;
        let maxDependencyCount = 0;
        let maxDependencyPlugin = '';
        for (const [pluginName, dependencies] of this.dependencyGraph) {
            const count = dependencies.length;
            totalDependencyCount += count;
            if (count > maxDependencyCount) {
                maxDependencyCount = count;
                maxDependencyPlugin = pluginName;
            }
            // 检查循环依赖
            const result = this.resolveDependencies(pluginName);
            stats.circularDependencies += result.circular.length;
        }
        stats.averageDependenciesPerPlugin = stats.totalPlugins > 0 ?
            totalDependencyCount / stats.totalPlugins : 0;
        stats.mostDependentPlugin = {
            name: maxDependencyPlugin,
            count: maxDependencyCount
        };
        return stats;
    }
    /**
     * 导出依赖图
     */
    exportDependencyGraph() {
        const graph = {};
        for (const [pluginName, dependencies] of this.dependencyGraph) {
            graph[pluginName] = dependencies.map(dep => ({
                name: dep.name,
                version: dep.version,
                required: dep.required,
                optional: dep.optional
            }));
        }
        return JSON.stringify(graph, null, 2);
    }
    /**
     * 导入依赖图
     */
    importDependencyGraph(graphJson) {
        try {
            const graph = JSON.parse(graphJson);
            for (const [pluginName, dependencies] of Object.entries(graph)) {
                this.dependencyGraph.set(pluginName, dependencies);
            }
            console.log('依赖图导入成功');
        }
        catch (error) {
            console.error('依赖图导入失败:', error);
        }
    }
    /**
     * 清空依赖管理器
     */
    clear() {
        this.registeredDependencies.clear();
        this.dependencyGraph.clear();
        this.injector.clear();
        this.loadOrder = [];
    }
}
exports.NexusDependencyManager = NexusDependencyManager;
// 依赖注入装饰器
function Injectable(token) {
    return function (target) {
        const dependencyManager = NexusDependencyManager.getInstance();
        dependencyManager.registerProvider({
            provide: token || target.name,
            useClass: target
        });
    };
}
// 注入装饰器
function Inject(token) {
    return function (target, propertyKey, parameterIndex) {
        // 这里可以添加参数注入的元数据
        console.log(`注入依赖 ${token.toString()} 到 ${target.name}`);
    };
}
// 导出单例实例
exports.DependencyManager = NexusDependencyManager.getInstance();
